import React, {
    useEffect,
    useMemo,
    useState,
    useCallback,
    useRef,
} from 'react';
import { Header } from '../common/Header';
import TableGrid from '../table-grid/TableGrid';
import { EndPoints } from '../../axios/endPoints';
import { GridColDef } from '@mui/x-data-grid';
import {
    Button,
    Grid,
    IconButton,
    Tooltip,
    Box,
    Typography,
    CircularProgress,
    Card,
    Divider,
    TextField,
    useTheme,
    useMediaQuery,
    LinearProgress,
} from '@mui/material';
import moment from 'moment';
import {
    RootState,
    useAppDispatch,
    useAppSelector,
} from '../../store/store';
import {
    handleRefreshMLTesterTable,
    handleToggleDeleteFilePopup,
    handleToggleFileDetailsPopup,
    handleToggleImportTrainingDataDialog,
    handleToggleFileDatasetPopup,
} from '../../store/mltester-slice/mltester';
import CloudDownloadTwoToneIcon from '@mui/icons-material/CloudDownloadTwoTone';
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone';
import { DownloadFile } from '../../services/FileServices';
import { useTranslation } from 'react-i18next';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import RedirectIconButton from '../common/RedirectIconButton';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import RefreshIcon from '@mui/icons-material/Refresh';
import ImportTesterTrainingData from './ImportTesterTrainingData';
import TableRowsIcon from '@mui/icons-material/TableRows';
import GridViewTwoToneIcon from '@mui/icons-material/GridViewTwoTone';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DatasetTwoToneIcon from '@mui/icons-material/DatasetTwoTone';
import { axiosInstance } from '../../axios/axiosHandler';
import qs from 'qs';
import { authHeader } from '../../store/auth-slice/auth-slice';
import { useDebounce } from 'use-debounce';
import { useDisplaySize } from '../../utils/useDisplaySize';
import TruncatedText from '../common/TruncatedText';

interface MLTesterProps {
    onSelectDataset: (rowData: any) => void;
}

type ViewMode = 'table' | 'cards';

interface ViewToolbarProps {
    viewMode: ViewMode;
    searchText: string;
    onImportClick: () => void;
    onRefreshClick: () => void;
    onToggleToTable: () => void;
    onToggleToCards: () => void;
    onSearchChange: (text: string) => void;
}
const ViewToolbar = React.memo<ViewToolbarProps>((props) => {
    const { t } = useTranslation();
    const {
        viewMode,
        onImportClick,
        onRefreshClick,
        onToggleToTable,
        onToggleToCards,
        searchText,
        onSearchChange,
    } = props;

    return (
        <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={6}>
                <Button
                    onClick={onImportClick}
                    variant="contained"
                    size="small"
                    className="button-color"
                >
                    {t('CREATOR_IMPORT_TESTING_DATA_BUTTON')}
                </Button>
                <Tooltip title={t('REFRESH_TABLE_BUTTON_TOOLTIP')}>
                    <IconButton onClick={onRefreshClick} sx={{ ml: 1, color: '#555' }}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
                <Box sx={{ mr: 2, width: { xs: '100%', sm: 'auto' } }}>
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder={t('VIEW_TOOLBAR_SEARCH')}
                        value={searchText}
                        onChange={(e: any) => onSearchChange(e.target.value)}
                    />
                </Box>
                <Tooltip title={t('VIEW_TOOLTIP_SHOW_AS_TABLE')}>
                    <IconButton
                        onClick={onToggleToTable}
                        sx={{ color: viewMode === 'table' ? 'var(--link-text-color)' : '#888' }}
                    >
                        <TableRowsIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('VIEW_TOOLTIP_SHOW_AS_CARDS')}>
                    <IconButton
                        onClick={onToggleToCards}
                        sx={{ ml: 1, color: viewMode === 'cards' ? 'var(--link-text-color)' : '#888' }}
                    >
                        <GridViewTwoToneIcon />
                    </IconButton>
                </Tooltip>
            </Grid>
        </Grid>
    );
});
ViewToolbar.displayName = 'ViewToolbar';

interface TableViewProps {
    columns: GridColDef[];
    refresh: number;
    onSelectDataset: (row: any) => void;
    selectedTesterRowId: any;
    searchText: string;
}
const TableView = React.memo<TableViewProps>((props) => {
    const { columns, refresh, onSelectDataset, selectedTesterRowId, searchText } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Box sx={{ mt: 2, height: { xs: 300, sm: 400, md: 500 }, width: '100%' }}>
            <TableGrid
                baseURL={EndPoints.ML_TESTER_GET_TESTING_DATASETS}
                columns={columns}
                sortColumns={[{ field: 'id', sort: 'asc' }]}
                refresh={refresh}
                disableRowSelection
                tableHeight={isMobile ? 300 : 500}
                searchText={searchText}
                onCellClick={(params: any) => {
                    if (params.field === 'name') {
                        onSelectDataset(params.row);
                    }
                }}
                getCellClassName={(params: any) => {
                    if (params.field === 'name') return 'custom-pointer-cell';
                    return '';
                }}
                rowHeight={35}
                columnHeaderHeight={40}
                getRowClassName={(params: any) =>params.id === selectedTesterRowId ? 'highlighted-row' : ''}
            />
        </Box>
  );
});
TableView.displayName = 'TableView';

const MiniDataChart: React.FC<{ rowCount: number; columnCount: number }> = ({
    rowCount,
    columnCount,
}) => {
    const { t } = useTranslation();
    const maxValue = Math.max(rowCount || 1, columnCount || 1);
    const rowPercentage = ((rowCount || 0) / maxValue) * 100;
    const columnPercentage = ((columnCount || 0) / maxValue) * 100;

    return (
        <Box sx={{ mt: 1.5, mb: 1.5 }}>
            <Box sx={{ mb: 0.8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                    <Typography variant="caption" sx={{ minWidth: '45px', fontSize: '0.7rem', color: '#555' }}>
                        {t('TESTER_ROW_COUNT_FIELD')}
                    </Typography>
                    <Typography variant="caption" sx={{ ml: 'auto', fontSize: '0.75rem', fontWeight: 600 }}>
                        {rowCount?.toLocaleString() || 0}
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={rowPercentage}
                    sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: 'color-mix(in srgb, var(--button-background-color) 15%, transparent)',
                        '& .MuiLinearProgress-bar': {
                        backgroundColor: 'var(--button-background-color)',
                        borderRadius: 3,
                        },
                    }}
                />
            </Box>
            <Box sx={{ mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                    <Typography variant="caption" sx={{ minWidth: '45px', fontSize: '0.7rem', color: '#555' }}>
                        {t('TESTER_COLUMN_COUNT_FIELD')}
                    </Typography>
                    <Typography variant="caption" sx={{ ml: 'auto', fontSize: '0.75rem', fontWeight: 600 }}>
                        {columnCount || 0}
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={columnPercentage}
                    sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: 'color-mix(in srgb, var(--button-background-color) 15%, transparent)',
                        '& .MuiLinearProgress-bar': {
                        backgroundColor: 'var(--button-background-color)',
                        borderRadius: 3,
                        },
                    }}
                />
            </Box>
        </Box>
    );
};

interface CardItemProps {
    row: any;
    onSelectDataset: (row: any) => void;
    dispatchToggleDetails: (fileId: string, fileName: string) => void;
    dispatchToggleDelete: (fileId: string, fileName: string) => void;
    dispatchToggleDataset: (datasetId: number, pageNumber: number, pageSize: number) => void;
}

const CardItem: React.FC<CardItemProps> = (props) => {
    const { t } = useTranslation();
    const { row, onSelectDataset, dispatchToggleDetails, dispatchToggleDelete, dispatchToggleDataset } = props;
    
    return (
        <Card
            sx={{
                flex: '1 0 0',
                p: { xs: 2, sm: 3 },
                mb: 2,
                borderRadius: 2,
                backgroundColor: '#fafafa',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: 1,
                '&:hover': {
                    boxShadow: 4,
                    backgroundColor: '#f5f5f5',
                },
                border: '1px solid rgba(0, 0, 0, 0.15)',
                minWidth: { xs: '100%', sm: 'auto' },
                maxWidth: '100%',
                width: '100%',
            }}
        >
            <Box sx={{ 
                overflowY: 'auto',
                minWidth: 0,
            }}>
                <TruncatedText
                    text={row.name || ''}
                    maxLength={13}
                    variant="h6"
                    onClick={() => onSelectDataset(row)}
                    sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: 'var(--link-text-color)',
                    }}
                />
                
                <TruncatedText
                    text={row.id || ''}
                    maxLength={10}
                    sx={{ mb: 1, color: '#333' }}
                >
                    <strong>{t('TESTER_ID_FIELD')}:</strong>{' '}
                </TruncatedText>
                
                <TruncatedText
                    text={row.fileName || ''}
                    maxLength={13}
                    sx={{ mb: 0.5, color: '#333' }}
                >
                    <strong>{t('TESTER_FILE_NAME_FIELD')}:</strong>{' '}
                </TruncatedText>
                
                <Typography
                    variant="body2"
                    sx={{ 
                        mb: 0.5, 
                        color: '#333',
                        wordBreak: 'break-all',
                        overflowWrap: 'break-word',
                    }}
                >
                <strong>{t('TESTER_UPLOAD_DATE_FIELD')}:</strong>{' '}
                    {moment.utc(row.uploadDate).local().format('DD/MM/YYYY HH:mm')}
                </Typography>
                
                <MiniDataChart
                    rowCount={row.rowCount}
                    columnCount={row.columnCount}
                />
            </Box>
            <Divider />
            <Box sx={{ mt: 2 }}>
                <Grid container spacing={1} display="flex" justifyContent="center">
                    { process.env.REACT_APP_COSMOS_ENABLE === "true" && (
                    <Grid item>
                        <Tooltip title="Data">
                        <IconButton size="small" onClick={() => dispatchToggleDataset(row.id, 0, 10)}>
                            <DatasetTwoToneIcon className="dataset-icon" />
                        </IconButton>
                        </Tooltip>
                    </Grid>)
                    }     
                    <Grid item>
                        <Tooltip title="Details">
                            <IconButton size="small" onClick={() => dispatchToggleDetails(row.id, row.fileName)}>
                                <InfoTwoToneIcon className="info-icon" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item>
                        <Tooltip title="Download">
                            <IconButton
                                size="small"
                                onClick={() =>
                                    DownloadFile(EndPoints.ML_TESTER_DOWNLOAD_TESTING_DATASET + `?id=${row.id}`)
                                }
                            >
                                <CloudDownloadTwoToneIcon className="download-icon" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item>
                        <Tooltip title="New Prediction">
                            <IconButton size="small" onClick={() => onSelectDataset(row)}>
                                <PlayCircleIcon className="play-icon" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item>
                        <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => dispatchToggleDelete(row.id, row.fileName)}>
                                <DeleteForeverTwoToneIcon className="delete-icon" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Box>
        </Card>
     );
};

const MemoCardItem = React.memo(CardItem);
MemoCardItem.displayName = 'CardItem';

interface CardCarouselProps {
    cardData: any[];
    cardLoading: boolean;
    cardTotalCount: number;
    cardPageIndex: number;
    totalCardPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
    onNextPage: () => void;
    onPrevPage: () => void;
    onSelectDataset: (row: any) => void;
    dispatchToggleDetails: (fileId: string, fileName: string) => void;
    dispatchToggleDelete: (fileId: string, fileName: string) => void;
    dispatchToggleDataset: (datasetId: number, pageNumber: number, pageSize: number) => void;

}
const CardCarousel: React.FC<CardCarouselProps> = (props) => {
    const {
        cardData,
        cardLoading,
        totalCardPages,
        hasNext,
        hasPrevious,
        onNextPage,
        onPrevPage,
        onSelectDataset,
        dispatchToggleDetails,
        dispatchToggleDelete,
        dispatchToggleDataset,
        cardPageIndex,
    } = props;
    const { t } = useTranslation();
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [fadeIn, setFadeIn] = useState(false);

    const showLoading = useMemo(() => cardLoading, [cardLoading]);
    const showEmpty = useMemo(() => !cardLoading && cardData.length === 0, [cardLoading, cardData.length]);
    
    useEffect(() => {
        if (cardLoading) {
            setIsTransitioning(true);
            setFadeIn(false);
        } else {
            const timer = setTimeout(() => {
                setIsTransitioning(false);
                setFadeIn(true);
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [cardLoading]);

    const stableCardData = useMemo(() => cardData, [cardData]);
    
    if (showLoading || isTransitioning) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    opacity: showLoading ? 0.8 : 1,
                    transition: 'opacity 0.3s ease-in-out',
                    transform: 'translateZ(0)',
                }}
            >
                <CircularProgress 
                    style={{ 
                        color: 'var(--button-background-color)',
                        transform: 'translateZ(0)', 
                    }} 
                />
            </Box>
        );
    }
    
    if (showEmpty) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    opacity: fadeIn ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                    transform: 'translateZ(0)',
                }}
            >
                <Typography 
                    variant="body2" 
                    sx={{ 
                        textAlign: 'center', 
                        color: '#666',
                        transform: 'translateZ(0)',
                    }}
                >
                    {t('CARD_CAROUSEL_NO_DATA_AVAILABLE')}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
            <IconButton
                onClick={onPrevPage}
                sx={{ color: totalCardPages > 1 ? 'var(--link-text-color)' : '#ccc' }}
                disabled={totalCardPages <= 1 || !hasPrevious}
            >
                <ChevronLeftIcon />
            </IconButton>
            <Box
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    height: '100%',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        gap: '16px',
                        justifyContent: 'center',
                        height: '100%',
                    }}
                >
                    {stableCardData.map((row) => (
                        <MemoCardItem
                            key={row.id}
                            row={row}
                            onSelectDataset={onSelectDataset}
                            dispatchToggleDetails={dispatchToggleDetails}
                            dispatchToggleDelete={dispatchToggleDelete}
                            dispatchToggleDataset={dispatchToggleDataset}
                        />
                    ))}
                </div>
            </Box>
            <IconButton
                onClick={onNextPage}
                sx={{ color: totalCardPages > 1 ? 'var(--link-text-color)' : '#ccc' }}
                disabled={totalCardPages <= 1 || !hasNext}
            >
                <ChevronRightIcon />
            </IconButton>
        </Box>
    );
};
CardCarousel.displayName = 'CardCarousel';

const MLTester: React.FC<MLTesterProps> = ({ onSelectDataset }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { refreshMLTesterTable, selectedTesterRow } = useAppSelector(
        (state: RootState) => state.mltester
    );
    const { cardsPerPage } = useDisplaySize();

    const [viewMode, setViewMode] = useState<ViewMode>(() => {
        const saved = window.localStorage.getItem('mlTesterViewMode');
        return saved === 'table' ? 'table' : 'cards';
    });
    const [cardData, setCardData] = useState<any[]>([]);
    const [cardLoading, setCardLoading] = useState<boolean>(false);
    const [cardRefreshTrigger, setCardRefreshTrigger] = useState<number>(0);
    const [cardPageIndex, setCardPageIndex] = useState<number>(0);
    const [cardTotalCount, setCardTotalCount] = useState<number>(0);
    const [cardHasNext, setCardHasNext] = useState<boolean>(false);
    const [cardHasPrevious, setCardHasPrevious] = useState<boolean>(false);
    const [searchInput, setSearchInput] = useState<string>('');
    
    const [debouncedSearchText] = useDebounce(searchInput, 500);
    
    const isInitialMount = useRef(true);

    const columns: GridColDef[] = useMemo(
        () => [
            { field: 'id', headerName: t('TESTER_ID_FIELD') },
            {
                field: 'name',
                headerName: t('TESTER_NAME_FIELD'),
                minWidth: 300,
                renderCell: (params) => <div className="styled-div">{params.value}</div>,
            },
            { field: 'fileName', headerName: t('TESTER_FILE_NAME_FIELD'), minWidth: 300 },
            { field: 'rowCount', headerName: t('TESTER_ROW_COUNT_FIELD'), minWidth: 150 },
            { field: 'columnCount', headerName: t('TESTER_COLUMN_COUNT_FIELD'), minWidth: 150 },
            {
                field: 'uploadDate',
                headerName: t('TESTER_UPLOAD_DATE_FIELD'),
                renderCell: (params) =>
                moment.utc(params.value).local().format('DD/MM/YYYY HH:mm'),
                minWidth: 250,
            },
            {
                field: 'actions',
                headerName: t('TESTER_ACTIONS_FIELD'),
                minWidth: 300,
                renderCell: (params) => (
                <Grid container spacing={1}>
                { process.env.REACT_APP_COSMOS_ENABLE === "true" && (
                    <Grid item>
                        <Tooltip title={t('TESTER_DATA_ACTION')}>
                        <IconButton
                            onClick={(e) => {
                            e.stopPropagation();
                            dispatch(
                                handleToggleFileDatasetPopup({
                                open: true,
                                body: {
                                    datasetId: params.row.id,
                                    pageNumber: 0,
                                    pageSize: 10
                                },
                                })
                            );
                            }}
                        >
                            <DatasetTwoToneIcon className="data-icon" />
                        </IconButton>
                        </Tooltip>
                    </Grid>)
                    }
                    <Grid item>
                        <Tooltip title={t('TESTER_DETAILS_ACTION')}>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    dispatch(
                                    handleToggleFileDetailsPopup({
                                        open: true,
                                        body: {
                                        fileId: params.row.id,
                                        fileName: params.row.fileName,
                                        },
                                    })
                                    );
                                }}
                            >
                                <InfoTwoToneIcon className="info-icon" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item>
                    <Tooltip title={t('TESTER_DOWNLOAD_ACTION')}>
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                DownloadFile(
                                EndPoints.ML_TESTER_DOWNLOAD_TESTING_DATASET + `?id=${params.row.id}`
                                );
                            }}
                        >
                            <CloudDownloadTwoToneIcon className="download-icon" />
                        </IconButton>
                    </Tooltip>
                    </Grid>
                    <Grid item>
                    <Tooltip title={t('TESTER_NEW_PREDICTION_ACTION')}>
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                onSelectDataset(params.row);
                            }}
                        >
                            <PlayCircleIcon className="play-icon" />
                        </IconButton>
                    </Tooltip>
                    </Grid>
                    <Grid item>
                    <Tooltip title={t('TESTER_DELETE_ACTION')}>
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                dispatch(
                                handleToggleDeleteFilePopup({
                                    open: true,
                                    body: {
                                    fileName: params.row.fileName,
                                    fileId: params.row.id,
                                    },
                                })
                                );
                            }}
                        >
                            <DeleteForeverTwoToneIcon className="delete-icon" />
                        </IconButton>
                    </Tooltip>
                    </Grid>
                </Grid>
                ),
            },
        ],
        [dispatch, t, onSelectDataset]
    );

    const fetchCardPage = useCallback(async () => {
        setCardLoading(true);
        try {
            const queryObj = {
                currentPage: cardPageIndex + 1,
                pageSize: cardsPerPage,
                searchText: debouncedSearchText.trim(),
                sortColumns: [{ name: 'id', sortDirection: 'asc' }],
            };

            const response = await axiosInstance.get(
                EndPoints.ML_TESTER_GET_TESTING_DATASETS,
                {
                headers: authHeader().headers,
                params: queryObj,
                paramsSerializer: (params) =>
                    qs.stringify(params, { encodeValuesOnly: true, allowDots: true }),
                }
            );

            setCardData(response.data.items);
            setCardTotalCount(response.data.totalCount);
            setCardHasNext(response.data.hasNext);
            setCardHasPrevious(response.data.hasPrevious);
        } catch (err) {
            console.error('Error fetching card page:', err);
            setCardData([]);
            setCardTotalCount(0);
        } finally {
            setCardLoading(false);
        }
    }, [cardPageIndex, debouncedSearchText, cardsPerPage]);
    
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        setCardPageIndex(0);
    }, [debouncedSearchText]);

    useEffect(() => {
        if (viewMode === 'cards') {
            fetchCardPage();
        }
    }, [viewMode, cardRefreshTrigger, cardPageIndex, refreshMLTesterTable, fetchCardPage]);

    useEffect(() => {
        setCardPageIndex(0);
    }, [cardsPerPage]);

    const handleRefreshClick = useCallback(() => {
        if (viewMode === 'table') {
            dispatch(handleRefreshMLTesterTable());
        } else {
            setCardRefreshTrigger((prev) => prev + 1);
        }
    }, [viewMode, dispatch]);

    const handleToggleToTable = useCallback(() => {
        if (viewMode !== 'table') {
            setViewMode('table');
            window.localStorage.setItem('mlTesterViewMode', 'table');
        }
    }, [viewMode]);

    const handleToggleToCards = useCallback(() => {
        if (viewMode !== 'cards') {
            setViewMode('cards');
            window.localStorage.setItem('mlTesterViewMode', 'cards');
            setCardPageIndex(0);
        }
    }, [viewMode]);

    const handleImportClick = useCallback(() => {
        dispatch(handleToggleImportTrainingDataDialog(true));
    }, [dispatch]);

    const totalCardPages = useMemo(() => Math.ceil(cardTotalCount / cardsPerPage), [cardTotalCount, cardsPerPage]);

    const handleNextPage = useCallback(() => {
        if (totalCardPages <= 1) return;
        setCardPageIndex((prev) => (prev + 1 < totalCardPages ? prev + 1 : 0));
    }, [totalCardPages]);

    const handlePrevPage = useCallback(() => {
        if (totalCardPages <= 1) return;
        setCardPageIndex((prev) => (prev - 1 >= 0 ? prev - 1 : totalCardPages - 1));
    }, [totalCardPages]);

    const dispatchToggleDetails = useCallback(
        (fileId: string, fileName: string) => {
            dispatch(
                handleToggleFileDetailsPopup({
                open: true,
                body: { fileId, fileName },
                })
            );
        },
        [dispatch]
    );
    
    const dispatchToggleDataset = useCallback(
    (datasetId: number, pageNumber: number, pageSize: number) => {
        dispatch(
        handleToggleFileDatasetPopup({
            open: true,
            body: { datasetId, pageNumber, pageSize },
        })
        );
    },
    [dispatch]
    )

    const dispatchToggleDelete = useCallback(
        (fileId: string, fileName: string) => {
            dispatch(
                handleToggleDeleteFilePopup({
                open: true,
                body: { fileId, fileName },
                })
            );
        },
        [dispatch]
    );

   
    const handleSearchChange = useCallback((text: string) => {
        setSearchInput(text);
    }, []);

    return (
        <Card sx={{ position: 'relative' }}>
            <Header
                title={t('SIDEBAR_ML_TESTER_TEXT')}
                helperIcon={
                <RedirectIconButton to={'/help/6'} className="helper-icon">
                    <ContactSupportIcon />
                </RedirectIconButton>
                }
            />
            <Box sx={{ p: 2 }}>
                <ViewToolbar
                    viewMode={viewMode}
                    onImportClick={handleImportClick}
                    onRefreshClick={handleRefreshClick}
                    onToggleToTable={handleToggleToTable}
                    onToggleToCards={handleToggleToCards}
                    searchText={searchInput}
                    onSearchChange={handleSearchChange}
                />
                {viewMode === 'table' ? (
                    <TableView
                        columns={columns}
                        refresh={refreshMLTesterTable}
                        onSelectDataset={onSelectDataset}
                        selectedTesterRowId={selectedTesterRow?.id}
                        searchText={debouncedSearchText}
                    />
                ) : (
                    <Box
                        sx={{
                            mt: 2,
                            height: { xs: 300, sm: 375, md: 375 },
                            display: 'flex',
                            alignItems: 'stretch',
                            justifyContent: 'center',
                        }}
                    >
                        <CardCarousel
                            cardData={cardData}
                            cardLoading={cardLoading}
                            cardTotalCount={cardTotalCount}
                            cardPageIndex={cardPageIndex}
                            totalCardPages={totalCardPages}
                            hasNext={cardHasNext}
                            hasPrevious={cardHasPrevious}
                            onNextPage={handleNextPage}
                            onPrevPage={handlePrevPage}
                            onSelectDataset={onSelectDataset}
                            dispatchToggleDetails={dispatchToggleDetails}
                            dispatchToggleDelete={dispatchToggleDelete}
                            dispatchToggleDataset={dispatchToggleDataset}
                        />
                    </Box>
                )}
            </Box>
            <ImportTesterTrainingData />
        </Card>
    );
};

export default MLTester;